import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CampText from '../CampText';
import { categoriesDelCas } from '../../utils/categories';

/**
 * Passa 2 — què separa l'avui del futur preferit.
 *
 * Tres comparacions, i les tres hi són senceres: on som, què impulsa el canvi i
 * l'entorn on s'aplica. Cadascuna es passa per les categories del cas —el
 * PESTEL, o les pròpies si el cas en té— perquè no se n'escapi cap.
 *
 * Va en taula i no en formulari perquè la comparació ara/després només es
 * llegeix si les dues columnes són de costat. La categoria és una etiqueta,
 * no una casella que calgui omplir: les files s'afegeixen lliurement.
 */

const DIMENSIONS = ['where', 'drivers', 'environment'];

const DifferencesStep = ({ case_, differences, onCreate, onPatch, onDelete }) => {
  const { t } = useTranslation();
  const categories = useMemo(() => categoriesDelCas(case_, t), [case_, t]);

  const perDimensio = (dim) => differences
    .filter(d => d.dimension === dim)
    .sort((a, b) => (a.position - b.position) || (a.id - b.id));

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900">{t('backcasting.step2Title')}</h3>
        <p className="mt-1 max-w-3xl text-sm text-neutral-600">{t('backcasting.step2Lead')}</p>
      </div>

      {DIMENSIONS.map(dim => {
        const files = perDimensio(dim);
        return (
          <section key={dim}>
            <div className="mb-3 flex items-center gap-3">
              <h4 className="whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-neutral-500">
                {t(`backcasting.dimension.${dim}`)}
              </h4>
              <span className="h-px flex-1 bg-neutral-200" />
              <button
                type="button"
                onClick={() => onCreate({ dimension: dim, position: files.length })}
                className="whitespace-nowrap rounded-md border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
              >
                {t('backcasting.addRow')}
              </button>
            </div>

            {files.length === 0 ? (
              <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500">
                {t(`backcasting.dimensionEmpty.${dim}`)}
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-neutral-200">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="w-40 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                        {t('backcasting.category')}
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                        {t(`backcasting.presentHeader.${dim}`)}
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                        {t(`backcasting.futureHeader.${dim}`)}
                      </th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {files.map(fila => (
                      <tr key={fila.id} className="border-b border-neutral-200 align-top last:border-b-0">
                        <td className="px-3 py-3">
                          <select
                            value={fila.category || ''}
                            onChange={(e) => onPatch(fila.id, { category: e.target.value || null })}
                            className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs text-neutral-700 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
                          >
                            <option value="">{t('backcasting.noCategory')}</option>
                            {categories.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-3">
                          <CampText
                            value={fila.present_text}
                            onSave={(v) => onPatch(fila.id, { present_text: v })}
                            multiline
                            rows={2}
                            inputClassName="text-sm"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <CampText
                            value={fila.future_text}
                            onSave={(v) => onPatch(fila.id, { future_text: v })}
                            multiline
                            rows={2}
                            inputClassName="text-sm"
                          />
                        </td>
                        <td className="px-2 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onDelete(fila.id)}
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-error-dark"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default DifferencesStep;
